import Big from 'big.js';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import SettingsActionsBanner from '../../SettingsActionsBanner';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import PrepaymentForm from './PrepaymentForm';
import { isPrepaymentLocked, prepaymentTypes } from './prepaymentTypes';

const InvoicedIcon = styled(Icon)`
  color: ${colors.primary};
`;

const LOCKED_TOOLTIP = "This prepayment has been invoiced and can't be modified.";

function availableOf(prepayment) {
  return Big(prepayment.fee ?? 0)
    .minus(prepayment.amountUsed ?? 0)
    .toNumber();
}

export default function Prepayments({ project }) {
  const auth = useAuth();
  const canEdit = project.permissions.edit && auth.projects.sections.billing;
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).prepayments().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (prepayment = null) => {
    setDialog(
      <PrepaymentForm
        currency={project.currency}
        projectId={project.id}
        prepaymentId={prepayment?.id}
        readOnly={!canEdit}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (prepayment) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Prepayment"
        resolve={async (result) => {
          if (result) {
            await api.www.workspaces(workspace.id).projectSettings(project.id).prepayments(prepayment.id).delete();
            toast.success('Prepayment deleted successfully.');
            await fetchData();
            selection.deselect(prepayment.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this prepayment?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  const totals = data.reduce(
    (acc, prepayment) => ({
      fee: Big(acc.fee)
        .plus(prepayment.fee ?? 0)
        .toNumber(),
      available: Big(acc.available).plus(availableOf(prepayment)).toNumber(),
    }),
    { fee: 0, available: 0 },
  );

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Prepayments</Page.Title>
        <TitleTooltip message="Schedule the billing of prepayments below. Prepayments are typically used to prebill on a Time & Materials project. Prepaid fees will automatically be used to offset future invoice amounts." />

        {canEdit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="prepayments_bulk_checkbox"
              selection={selection}
              isVisible={canEdit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Bill Date</ListView.Column>
            <ListView.Column width="8rem">Type</ListView.Column>
            <ListView.Column minWidth="10rem">Invoice Item</ListView.Column>
            <ListView.Column minWidth="16rem">Description</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListView.Column width="10rem" align="right">
              Available
            </ListView.Column>
            <ListView.Column width="3rem" />
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {data.map((prepayment) => {
              const locked = isPrepaymentLocked(prepayment);
              const tooltip = locked ? LOCKED_TOOLTIP : undefined;
              const typeName = prepaymentTypes.find((type) => type.id === prepayment.type)?.name ?? '';

              return (
                <ListView.Row key={prepayment.id} onClick={() => handleShowForm(prepayment)}>
                  <ListViewSelection.Cell id={prepayment.id} selection={selection} testId="checkbox_prepayment" />
                  <ListView.Cell>
                    <DateTime value={prepayment.date} />
                  </ListView.Cell>
                  <ListView.Cell>{typeName}</ListView.Cell>
                  <ListView.Cell>{prepayment.invoiceItem?.name}</ListView.Cell>
                  <ListView.Cell>{prepayment.details}</ListView.Cell>
                  <ListView.Cell>
                    <Currency value={prepayment.fee} currency={prepayment.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <Currency value={availableOf(prepayment)} currency={prepayment.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {locked && (
                      <Tooltip message={LOCKED_TOOLTIP}>
                        <InvoicedIcon icon="file-invoice-dollar" />
                      </Tooltip>
                    )}
                  </ListView.Cell>
                  <ListViewActions>
                    {canEdit ? (
                      <>
                        {locked ? (
                          <ListViewActions.View onClick={() => handleShowForm(prepayment)} />
                        ) : (
                          <ListViewActions.Edit onClick={() => handleShowForm(prepayment)} />
                        )}
                        <hr />
                        <ListViewMenu>
                          <ListViewMenu.Item onClick={() => handleShowForm(prepayment)}>
                            {locked ? 'View' : 'Edit'}
                          </ListViewMenu.Item>

                          <ListViewMenu.DeleteItem
                            disabled={locked}
                            tooltip={tooltip}
                            onClick={() => handleDelete(prepayment)}>
                            Delete
                          </ListViewMenu.DeleteItem>
                        </ListViewMenu>
                      </>
                    ) : (
                      <ListViewActions.View onClick={() => handleShowForm(prepayment)} />
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <>
                <ListView.TotalRow>
                  <ListView.Cell />
                  <ListView.Cell>Total</ListView.Cell>
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListView.Cell>
                    <Currency value={totals.fee} currency={project.currency} />
                  </ListView.Cell>
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListViewActions.Cell />
                </ListView.TotalRow>

                <ListView.TotalRow>
                  <ListView.Cell />
                  <ListView.Cell>Available</ListView.Cell>
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListView.Cell />
                  <ListView.Cell>
                    <Currency value={totals.available} currency={project.currency} />
                  </ListView.Cell>
                  <ListView.Cell />
                  <ListViewActions.Cell />
                </ListView.TotalRow>
              </>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Prepayment" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
